"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Logger = void 0;
const util_1 = require("util");
const json_stringify_safe_1 = __importDefault(require("json-stringify-safe"));
const log_severity_type_1 = require("./log-severity.type");
const should_output_json_1 = require("./should-output-json");
const should_output_pretty_json_1 = require("./should-output-pretty-json");
const SHOULD_OUTPUT_PRETTY_JSON = (0, should_output_pretty_json_1.shouldOutputPrettyJson)();
const SHOULD_OUTPUT_JSON = (0, should_output_json_1.shouldOutputJson)();
class Logger {
    constructor(options) {
        const { app, cid, client, env, operationName, userId } = options;
        this.logContext = {
            app,
            client,
            cid: cid || "no-correlation",
            env,
            operationName,
            userId,
        };
        this.logger = console;
    }
    log(log, severity = log_severity_type_1.LogSeverity.Info) {
        if (!this.shouldLog(severity)) {
            return;
        }
        const { message, ...payload } = this.prepareLogPayload(log);
        const logMessage = this.prepareLogEntry({
            severity,
            payload,
            message,
            ...this.logContext,
        });
        this.logger.log(logMessage);
    }
    debug(log) {
        this.log(log, log_severity_type_1.LogSeverity.Debug);
    }
    info(log) {
        this.log(log, log_severity_type_1.LogSeverity.Info);
    }
    warn(log) {
        this.log(log, log_severity_type_1.LogSeverity.Warn);
    }
    error(log) {
        this.log(log, log_severity_type_1.LogSeverity.Error);
    }
    validationError(validationErrors) {
        this.log({
            message: "Validation error",
            validationErrors: (0, util_1.inspect)(validationErrors, false, 10),
        }, log_severity_type_1.LogSeverity.Error);
    }
    prepareLogEntry(entry) {
        const replacer = (key, value) => {
            if (key === "password") {
                return "********";
            }
            return value;
        };
        if (SHOULD_OUTPUT_JSON) {
            try {
                const output = SHOULD_OUTPUT_PRETTY_JSON
                    ? JSON.stringify(entry, replacer, 2)
                    : JSON.stringify(entry, replacer);
                return output;
            }
            catch (err) {
                return (0, json_stringify_safe_1.default)(entry);
            }
        }
        return entry;
    }
    prepareLogPayload(log = {}) {
        if (typeof log === "string") {
            return { message: log };
        }
        let loggingData = log;
        // Ensure expected logging data structure
        if (log instanceof Error) {
            loggingData = { err: log };
        }
        else if (!log.err && log.payload && log.payload instanceof Error) {
            loggingData.err = log.payload;
            loggingData.payload = undefined;
        }
        else if (log.payload && (typeof log.payload !== "object" || Array.isArray(log.payload))) {
            loggingData.payload = { data: log.payload };
        }
        // This weird thing makes sure we can use both the "legacy" interface of the logger with an explicit payload object
        // and the "new" openended interface where you can log anything
        const { payload = {}, message, err, error, ...rest } = loggingData;
        // Looking for an error object in the passed data.
        const e = err || error || payload.err || payload.error;
        // Ensure no error object is in the payload.
        if (payload.err) {
            payload.err = undefined;
        }
        if (payload.error) {
            payload.error = undefined;
        }
        // Because JSON.stringify(new Error("Reason")) gives "{}",
        // the error object should be re-created as a plain object, if any.
        // More details https://stackoverflow.com/questions/18391212/is-it-not-possible-to-stringify-an-error-using-json-stringify
        let plainErrorObject;
        if (e) {
            if (e instanceof Error) {
                plainErrorObject = {
                    message: e.message,
                    stack: this.beautifyStackTrace(e.stack),
                };
            }
            else {
                plainErrorObject = {
                    message: e,
                };
            }
        }
        if (payload.stack) {
            payload.stack = this.beautifyStackTrace(payload.stack);
        }
        return {
            ...payload,
            message: message || plainErrorObject?.message,
            error: plainErrorObject,
            ...rest,
        };
    }
    shouldLog(severity) {
        const logLevels = [
            log_severity_type_1.LogSeverity.Error,
            log_severity_type_1.LogSeverity.Warn,
            log_severity_type_1.LogSeverity.Info,
            log_severity_type_1.LogSeverity.Debug,
            log_severity_type_1.LogSeverity.Undefined,
        ];
        const severityLevel = logLevels.findIndex((level) => level === severity);
        const allowableLevel = logLevels.findIndex((level) => level === "Debug");
        return severityLevel <= allowableLevel;
    }
    beautifyStackTrace(stacks) {
        if (!stacks || process.env.NODE_ENV !== "production") {
            return stacks;
        }
        const [message, ...frames] = stacks.split("\n");
        const usefullFrames = frames.filter((frame) => !frame.includes("node_modules"));
        return [message, ...usefullFrames].join("\n");
    }
}
exports.Logger = Logger;
//# sourceMappingURL=logger.js.map